import React, { useState, useRef, useEffect } from 'react';
import {
  ChevronDown, Flame, Star, Shield, TrendingUp, Brain,
  Heart, Compass, AlertTriangle, Check, X, MapPin,
  Calendar, Clock, MessageCircle, ArrowRight, Zap,
  Target, Users, BookOpen, Award
} from 'lucide-react';

const WA_LINK = "https://wa.me/601171383699";

const AnimatedSection = ({ children, className = "", id }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section ref={ref} id={id} className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}>
      {children}
    </section>
  );
};

const animalImages = {
  Rat: "https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/01si8ptq_Rat.png",
  Pig: "https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/930teane_pig.png",
  Dog: "https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/1n97d7y5_dog.png",
  Rooster: "https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/5swwd4v3_rooster.png",
  Monkey: "https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/djhjbq7t_monkey.png",
  Goat: "https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/ba20xdxo_Goat.png",
  Horse: "https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/g1ferleh_Horse.png",
  Snake: "https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/8wu5d5u8_Snake.png",
  Dragon: "https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/h0dnsc3q_Dragon.png",
  Rabbit: "https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/5egletr6_Rabbit.png",
  Tiger: "https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/87ybwi8q_Tiger.png",
  Ox: "https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/8bfu4yjl_Ox.png",
};

const animals = [
  { name: "Rat", stars: 5, themes: ["Old patterns collapse for reinvention", "Guidance and mentorship will appear", "Unexpected disruptions will occur"] },
  { name: "Ox", stars: 2, themes: ["Increase in authority and leadership", "Conflicts tend to resolve more smoothly", "Emotional fatigue and ego become a risk"] },
  { name: "Tiger", stars: 1, themes: ["Increased pressure and opposition", "Opportunities to gain knowledge and upskill", "Emotions will be heightened"] },
  { name: "Rabbit", stars: 5, themes: ["Opportunities align with smoother progress", "More charismatic, drawing support", "Misunderstandings may occur"] },
  { name: "Dragon", stars: 3, themes: ["A clean reset, old burdens dissolving", "Increased recognition and opportunities", "Require more emotional control"] },
  { name: "Snake", stars: 4, themes: ["Gain traction in ventures and leadership", "More authority and professional recognition", "Workload and communication friction"] },
  { name: "Horse", stars: 3, themes: ["Leadership and heavier responsibilities", "Financial growth with discipline and planning", "Self-sabotage if you hesitate or overthink"] },
  { name: "Goat", stars: 4, themes: ["Alliances, reconciliation, and partnerships", "Opportunities through collaboration", "Emotional dips when boundaries not set"] },
  { name: "Monkey", stars: 3, themes: ["Timely decision-making creates opportunities", "Suitable for movement and expansion", "Mental speed and idea flow heightened"] },
  { name: "Rooster", stars: 4, themes: ["More access to connections and helpful people", "More visibility and relationship opportunities", "Opportunities lost through neglect"] },
  { name: "Dog", stars: 3, themes: ["Conflicts and past complications resolve", "Achievements compound with consistent effort", "Mental clutter resurfaces if not grounded"] },
  { name: "Pig", stars: 3, themes: ["Opportunities in long-term projects", "Conflicts smooth out, help comes easily", "Increased spending through investments"] },
];

const fengShuiGrid = [
  { num: 4, color: "bg-green-600", label: "GREEN", name: "Academic", dir: "NE", type: "good" },
  { num: 9, color: "bg-purple-600", label: "PURPLE", name: "Investment", dir: "SE", type: "good" },
  { num: 8, color: "bg-gray-100 text-gray-800", label: "WHITE", name: "Wealth", dir: "E", type: "good" },
  { num: 6, color: "bg-gray-100 text-gray-800", label: "WHITE", name: "Career", dir: "N", type: "good" },
  { num: 1, color: "bg-gray-100 text-gray-800", label: "WHITE", name: "Noble People", dir: "Center", type: "good" },
  { num: 5, color: "bg-yellow-500 text-black", label: "YELLOW", name: "Calamity", dir: "S", type: "bad" },
  { num: 7, color: "bg-red-600", label: "RED", name: "Discord", dir: "SW", type: "bad" },
  { num: 3, color: "bg-teal-600", label: "JADE", name: "Arguments", dir: "W", type: "bad" },
  { num: 2, color: "bg-gray-800", label: "BLACK", name: "Illness", dir: "NW", type: "bad" },
];

const bonusEnergies = [
  { name: "The Creative Builder", id: "Eating God (EG)", career: "Take advantage of increased creativity to come up with new ideas and solve long-running issues.", relationships: "Expect lively, affectionate, and emotionally expressive relationships.", growth: "Balance pleasure with discipline. Channel creativity into something tangible.", watch: "Overindulgence, comfort-driven procrastination, and perfectionism." },
  { name: "The Challenger", id: "Hurting Officer (HO)", career: "Improve broken systems and introduce new approaches through influence and communication.", relationships: "Make an impression through words. Honesty matters, but so does delivery.", growth: "Develop skills in verbal expression, public speaking, and personal branding.", watch: "Speaking impulsively or creating unnecessary conflict." },
  { name: "The Connector", id: "Friend (F)", career: "Build alliances, expand your network, and collaborate on new projects.", relationships: "Seek equal partnerships with mutual respect and independence.", growth: "Strengthen self-reliance while learning when to cooperate strategically.", watch: "Stubbornness, unnecessary comparison, or losing direction." },
  { name: "The Leading Competitor", id: "Rob Wealth (RW)", career: "Take charge, lead teams, and thrive in healthy competitive environments.", relationships: "Lead with passion, but avoid dominance or ego-driven behaviour.", growth: "Sharpen your competitive edge while practising emotional awareness.", watch: "Ego, impulsiveness, power struggles, and uncontrolled spending." },
  { name: "The Intuitive Thinker", id: "Indirect Resource (IR)", career: "Explore the unconventional route for ideas, innovation, and direction.", relationships: "Open communication prevents distance and misunderstanding.", growth: "Learn through experience and dialogue, not just books or theory.", watch: "Ideas becoming impractical and mental isolation under pressure." },
  { name: "The Strategic Analyser", id: "Direct Resource (DR)", career: "Research your market thoroughly. Let data and analysis guide decisions.", relationships: "Expect relationships built on stability and practical support.", growth: "A favourable period for formal education and structured development.", watch: "Overthinking, passivity, and staying in preparation mode." },
  { name: "The Fighter", id: "Seven Killings (7K)", career: "Quick action, decisive problem-solving, and calculated risk-taking rewarded.", relationships: "Connections tend to be passionate and intense.", growth: "Treat pressure as fuel for growth. Build inner authority through challenge.", watch: "Blind risk-taking, reactive aggression, and short temper." },
  { name: "The Methodical Diplomat", id: "Direct Officer (DO)", career: "Focus on creating structure, systems, and order within teams.", relationships: "Show love through dependability and steady commitment.", growth: "Reorganise goals and set clear routines. Practice flexibility.", watch: "Rigidity, perfectionism, and resisting new ideas." },
  { name: "The Opportunity Seeker", id: "Indirect Wealth (IW)", career: "Explore new income streams, side hustles, or flexible business models.", relationships: "Set clear boundaries so short-term thrills don't create complications.", growth: "Pair instinct with due diligence for sustainable efforts.", watch: "Impatience, mood swings, and treating opportunity like a gamble." },
  { name: "The Structured Builder", id: "Direct Wealth (DW)", career: "Take operational control. Focus on measurable outcomes and financial performance.", relationships: "Expect loyalty, dependability, and responsible partnership.", growth: "Develop disciplined budgeting and prudent investment habits.", watch: "Micromanaging, stinginess, and prioritising efficiency over connection." },
];

const strategicDates = [
  { date: "20 February", times: ["3 AM - 4:59 AM", "5 AM - 6:59 AM", "3 PM - 4:59 PM"], note: "Not suitable for Goat year" },
  { date: "21 February", times: ["11 PM - 12:59 AM", "1 AM - 2:59 AM"], note: "Not suitable for Monkey year" },
  { date: "23 February", times: ["9 AM - 10:59 AM", "3 PM - 6:59 PM"], note: "Not suitable for Dog year" },
  { date: "25 February", times: ["5 AM - 6:59 AM", "3 PM - 6:59 PM"], note: "Not suitable for Rat year" },
];

const CrimsonHorsePage = () => {
  const [openBonus, setOpenBonus] = useState(null);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden pt-16" style={{ overflowX: 'clip' }}>

      {/* ===== HERO ===== */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden" data-testid="hero">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/1pw2yzfa_ChatGPT%20Image%20Apr%2029%2C%202026%2C%2002_18_37%20PM.png" alt="" aria-hidden="true" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-[#0a0a0a]" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <p className="text-red-400/80 text-sm uppercase tracking-[0.3em] font-semibold mb-4">Life Decoder Exclusive</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05] mb-4">
            <span className="text-white">GUIDE TO THE</span><br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400">CRIMSON HORSE</span>
          </h1>
          <p className="text-gray-300 text-lg sm:text-xl max-w-2xl mx-auto mb-8 leading-relaxed">
            Thriving Amid Chaos, Speed & High-Stakes Decisions in 2026
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400 text-white font-bold uppercase tracking-widest rounded-lg px-10 py-5 text-base transition-all hover:scale-[1.02]">
              <MessageCircle className="w-5 h-5" /> ENQUIRE NOW <ArrowRight className="w-5 h-5" />
            </a>
            <a href="#overview" className="text-red-400 hover:text-red-300 font-semibold flex items-center gap-1 py-4">
              Explore Guide <ChevronDown className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <AnimatedSection id="overview" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-center">
            Overview of the <span className="text-red-400">Crimson Horse Year</span>
          </h2>
          <p className="text-center text-orange-400 text-sm uppercase tracking-widest mb-8">When speed, courage, and emotional mastery decide everything</p>
          <div className="space-y-4 text-gray-300 leading-relaxed max-w-3xl mx-auto">
            <p>2026 marks the arrival of the <strong className="text-white">Crimson Horse Year</strong>, a year defined by intensity, visibility, and speed. This isn't a bad year, but the advantage goes to those who can respond decisively instead of waiting for the perfect condition.</p>
            <p>With extremely strong Fire energy and little room for pause, the world moves before it thinks. In this environment, your forecast helps you see what's coming, so you can act decisively instead of reacting late.</p>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== CHALLENGES & OPPORTUNITIES ===== */}
      <AnimatedSection className="py-20 px-6 bg-[#111]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-center">
            Challenges & <span className="text-red-400">Opportunities</span> of 2026
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-6"><TrendingUp className="w-6 h-6 text-green-400" /><h3 className="text-xl font-bold text-green-400">Opportunities</h3></div>
              <div className="space-y-4">
                {[
                  ["Reset of the playing field", "Old systems weaken, creating space for new players and fresh ideas"],
                  ["Start your own business", "Smaller, flexible setups move faster and adapt more easily"],
                  ["Learn high-demand skills", "Focus on skills that support your goals, not what's trending"],
                  ["Emotional & spiritual growth", "Inner stability becomes a strength"],
                  ["Fast, quality decision-making", "2026 favours quick, decisive action"],
                ].map(([t, d], i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                    <div><p className="text-white font-semibold text-sm">{t}</p><p className="text-gray-400 text-xs">{d}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-6"><AlertTriangle className="w-6 h-6 text-red-400" /><h3 className="text-xl font-bold text-red-400">Challenges</h3></div>
              <div className="space-y-4">
                {[
                  ["Chaos", "Situations change quickly, with little warning"],
                  ["Political upheaval", "Shifting policies and unstable leadership"],
                  ["Climate disasters", "Extreme fire energy leading to heat waves and imbalance"],
                  ["Rapid technological change", "AI bubble cools while innovation grows"],
                  ["Emotional decision making", "Stress and impatience driving poor choices"],
                ].map(([t, d], i) => (
                  <div key={i} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <div><p className="text-white font-semibold text-sm">{t}</p><p className="text-gray-400 text-xs">{d}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== 12 ANIMAL FORECAST ===== */}
      <AnimatedSection id="animals" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-center">
            12 Animal <span className="text-red-400">Forecast</span> for 2026
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-lg mx-auto">Key themes to watch for each animal sign in the Crimson Horse year</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 overflow-hidden">
            {animals.map((a, i) => (
              <div key={i} className="relative bg-[#B91C1C] rounded-2xl p-6 overflow-hidden hover:shadow-[0_8px_30px_-10px_rgba(185,28,28,0.5)] transition-all group" data-testid={`animal-${a.name.toLowerCase()}`}>
                {/* Gold character on top + image bg */}
                <div className="absolute right-3 top-3 text-[120px] leading-none font-serif text-[#D4A017]/25 group-hover:text-[#D4A017]/40 transition-colors select-none pointer-events-none">
                  {{"Rat":"鼠","Ox":"牛","Tiger":"虎","Rabbit":"兔","Dragon":"龍","Snake":"蛇","Horse":"馬","Goat":"羊","Monkey":"猴","Rooster":"雞","Dog":"狗","Pig":"豬"}[a.name]}
                </div>
                {animalImages[a.name] && (
                  <img src={animalImages[a.name]} alt={a.name} className="absolute right-[-5px] bottom-[-5px] w-[132px] h-[132px] object-contain opacity-30 group-hover:opacity-50 transition-opacity select-none pointer-events-none" />
                )}
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <h3 className="text-white font-black text-xl uppercase tracking-wider">{a.name}</h3>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className={`w-4 h-4 ${j < a.stars ? 'text-[#D4A017] fill-[#D4A017]' : 'text-white/30'}`} />
                      ))}
                    </div>
                  </div>
                  <div className="space-y-3">
                    {a.themes.map((t, j) => (
                      <p key={j} className="text-white/90 text-sm font-medium flex items-start gap-2">
                        <span className="text-[#D4A017] text-lg leading-none mt-[-2px] shrink-0">.</span>{t}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== FENG SHUI 2026 ===== */}
      <AnimatedSection id="fengshui" className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/h9nk36gx_ChatGPT%20Image%20Apr%2029%2C%202026%2C%2004_27_27%20PM.png" alt="" aria-hidden="true" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black mb-4 text-center text-[#1a1a1a] drop-shadow-sm">
            PERSONAL FENG SHUI <span className="text-red-700">ACTIVATION</span>
          </h2>
          <p className="text-[#8B6914] text-center text-base uppercase tracking-widest font-bold mb-8">Nine Flying Stars 2026</p>

          {/* Nine Flying Stars Grid */}
          <div className="max-w-2xl mx-auto mb-12">
            <img
              src="https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/jzav38vi_ChatGPT%20Image%20Apr%2029%2C%202026%2C%2003_22_06%20PM.png"
              alt="Nine Flying Stars Grid 2026"
              className="w-full rounded-2xl"
            />
          </div>

          <p className="text-[#5a5040] text-center max-w-2xl mx-auto mb-12 text-base">Align your environment to support how you think, feel, and act. When your space works with 2026's energies, stress reduces and decisions become clearer.</p>

          {/* Activate */}
          <h3 className="text-2xl font-bold text-green-800 mb-5 flex items-center gap-2"><Check className="w-6 h-6" /> Favourable Stars to Activate</h3>
          <div className="grid sm:grid-cols-2 gap-5 mb-12">
            {[
              { num: 4, color: "border-green-700/40 bg-white/85", label: "GREEN", name: "Academic", dir: "NORTHEAST", goal: "Learn new skills or expand knowledge", tip: "Study, take courses, read, or plan skill upgrades in this area." },
              { num: 6, color: "border-gray-500/40 bg-white/85", label: "WHITE", name: "Career/Authority", dir: "NORTH", goal: "Climb the career ladder", tip: "Work on leadership tasks, planning, and decision-making here." },
              { num: 8, color: "border-gray-400/40 bg-white/85", label: "WHITE", name: "Wealth", dir: "EAST", goal: "Grow your wealth", tip: "Focus on income-generating work like sales and promotions." },
              { num: 9, color: "border-purple-700/40 bg-white/85", label: "PURPLE", name: "Investment", dir: "SOUTHEAST", goal: "Venture into investments", tip: "Research and plan long-term investments here." },
            ].map((s, i) => (
              <div key={i} className={`p-6 rounded-xl border backdrop-blur-sm ${s.color}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl font-black text-[#1a1a1a]/20">{s.num}</span>
                  <div><p className="text-[#1a1a1a] font-bold text-base">{s.name}</p><p className="text-[#5a5040] text-sm">{s.label} - {s.dir}</p></div>
                </div>
                <p className="text-green-800 text-sm font-semibold mb-1">GOAL: {s.goal}</p>
                <p className="text-[#5a5040] text-sm">{s.tip}</p>
              </div>
            ))}
          </div>

          {/* Caution */}
          <h3 className="text-2xl font-bold text-red-800 mb-5 flex items-center gap-2"><AlertTriangle className="w-6 h-6" /> Areas of Caution</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            {[
              { num: 2, color: "border-gray-600/40 bg-white/85", label: "BLACK", name: "Illness", dir: "NORTHWEST", caution: "Health and energy", tip: "Keep quiet. Place heavy metal objects to neutralise energy." },
              { num: 3, color: "border-teal-600/40 bg-white/85", label: "JADE", name: "Arguments", dir: "WEST", caution: "Arguments and tension", tip: "Place oil lamps to cushion the negative energy." },
              { num: 5, color: "border-yellow-600/40 bg-white/85", label: "YELLOW", name: "Calamity", dir: "SOUTH", caution: "Accidents and setbacks", tip: "Keep quiet. Place heavy metal objects to neutralise energy." },
              { num: 7, color: "border-red-600/40 bg-white/85", label: "RED", name: "Discord", dir: "SOUTHWEST", caution: "Disputes and betrayals", tip: "Avoid sensitive financial discussions. Store valuables elsewhere." },
            ].map((s, i) => (
              <div key={i} className={`p-6 rounded-xl border backdrop-blur-sm ${s.color}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl font-black text-[#1a1a1a]/20">{s.num}</span>
                  <div><p className="text-[#1a1a1a] font-bold text-base">{s.name}</p><p className="text-[#5a5040] text-sm">{s.label} - {s.dir}</p></div>
                </div>
                <p className="text-red-800 text-sm font-semibold mb-1">CAUTION: {s.caution}</p>
                <p className="text-[#5a5040] text-sm">Remedy: {s.tip}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== DESK FENG SHUI - DRAGON & TIGER ===== */}
      {/* Banner - Desktop */}
      <div className="w-full hidden sm:block">
        <img
          src="https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/fr9n5ig3_Untitled.jpg"
          alt="Green Dragon and White Tiger - Personal Desktop Feng Shui Audit"
          className="w-full block"
        />
      </div>
      {/* Banner - Mobile */}
      <div className="w-full block sm:hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/a4qwxk1r_ChatGPT%20Image%20Apr%2029%2C%202026%2C%2004_14_53%20PM.png"
          alt="Green Dragon and White Tiger - Personal Desktop Feng Shui Audit"
          className="w-full block"
        />
      </div>
      <AnimatedSection className="pb-20 pt-12 px-6 bg-[#e7dcc7]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-center">
            <span className="text-[#2D5016]">Green Dragon</span> & <span className="text-[#4a4a4a]">White Tiger</span> <span className="text-[#1a1a1a]">Formation</span>
          </h2>
          <p className="text-[#5a5040] text-center max-w-lg mx-auto mb-12">Balance of Taichi — align your desk for productivity, wealth, and protection.</p>

          <div className="grid md:grid-cols-3 gap-8 items-start">
            {/* Green Dragon */}
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold text-[#2D5016] mb-1">GREEN DRAGON</h3>
              <p className="text-xs text-[#5a5040] uppercase tracking-widest mb-4">Left Side · 青龍</p>
              <div className="space-y-2 mb-5">
                {["Movement", "Growth", "Support", "Expansion"].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 md:justify-start justify-center">
                    <Check className="w-4 h-4 text-[#2D5016]" />
                    <span className="text-[#1a1a1a] font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[#2D5016] text-sm font-semibold">Place: Lamp, Speaker, Plant, Phone</p>
            </div>

            {/* Center - Taichi */}
            <div className="text-center flex flex-col items-center justify-center">
              <div className="text-5xl mb-3">☯</div>
              <h3 className="text-xl font-bold text-[#1a1a1a] mb-2">Balance of <span className="text-[#2D5016]">Taichi</span></h3>
              <p className="text-[#5a5040] text-sm mb-4 max-w-xs mx-auto">
                On your left… the Green Dragon.<br />On your right… the White Tiger.
              </p>
              <p className="text-3xl font-serif text-[#8B6914]">太 极 平 衡</p>
            </div>

            {/* White Tiger */}
            <div className="text-center md:text-right">
              <h3 className="text-2xl font-bold text-[#4a4a4a] mb-1">WHITE TIGER</h3>
              <p className="text-xs text-[#8B8B7A] uppercase tracking-widest mb-4">Right Side · 白虎</p>
              <div className="space-y-2 mb-5">
                {["Stability", "Control", "Structure", "Protection"].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 md:justify-end justify-center">
                    <span className="text-[#1a1a1a] font-medium">{item}</span>
                    <Check className="w-4 h-4 text-[#8B8B7A]" />
                  </div>
                ))}
              </div>
              <p className="text-[#5a5040] text-sm font-semibold">Place: Stationery, Books, Pens</p>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== BONUS ENERGIES ===== */}
      <AnimatedSection id="bonus" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-center">
            Your <span className="text-red-400">Bonus Energy</span> for 2026
          </h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12 text-sm">Click any profile on the wheel to reveal how the Crimson Horse ignites your energy for 2026.</p>

          <div className="flex flex-col lg:flex-row items-center gap-8">
            {/* Interactive Wheel - matching the reference design */}
            <div className="relative w-[340px] h-[340px] sm:w-[480px] sm:h-[480px] shrink-0 mx-auto">
              <style>{`
                .wheel-segment { transition: all 0.2s ease; cursor: pointer; }
                .wheel-segment:hover { filter: brightness(1.2); }
              `}</style>
              <svg viewBox="0 0 500 500" className="w-full h-full">
                {/* Outer gold ring */}
                <circle cx="250" cy="250" r="248" fill="#8B6914" />
                <circle cx="250" cy="250" r="245" fill="#B8860B" />
                {/* Middle grey ring */}
                <circle cx="250" cy="250" r="195" fill="#d0d0d0" />
                {/* Inner light ring */}
                <circle cx="250" cy="250" r="105" fill="#e8e8e8" />
                {/* Center dark circle */}
                <circle cx="250" cy="250" r="72" fill="#2a2a2a" />

                {/* Outer category dividers */}
                {[0, 72, 144, 216, 288].map((angle, i) => {
                  const rad = (angle - 90) * Math.PI / 180;
                  return <line key={i} x1={250 + 195 * Math.cos(rad)} y1={250 + 195 * Math.sin(rad)} x2={250 + 245 * Math.cos(rad)} y2={250 + 245 * Math.sin(rad)} stroke="#6B5310" strokeWidth="2" />;
                })}

                {/* Profile segment dividers */}
                {Array.from({length: 10}).map((_, i) => {
                  const rad = (i * 36 - 90) * Math.PI / 180;
                  return <line key={i} x1={250 + 72 * Math.cos(rad)} y1={250 + 72 * Math.sin(rad)} x2={250 + 195 * Math.cos(rad)} y2={250 + 195 * Math.sin(rad)} stroke="#aaa" strokeWidth="0.8" />;
                })}

                {/* Category labels on outer gold ring - arranged along radius */}
                {[
                  { label: "ANALYSIS", angle: 0 },
                  { label: "PEOPLE", angle: 72 },
                  { label: "IDEA", angle: 144 },
                  { label: "ACTION", angle: 216 },
                  { label: "PROCESS", angle: 288 },
                ].map((cat, ci) => {
                  const midRad = (cat.angle + 36 - 90) * Math.PI / 180;
                  const tx = 250 + 220 * Math.cos(midRad);
                  const ty = 250 + 220 * Math.sin(midRad);
                  const rot = cat.angle + 36;
                  return (
                    <text key={ci} x={tx} y={ty} textAnchor="middle" dominantBaseline="central"
                      fill="#1a1a1a" fontSize="18" fontWeight="900" letterSpacing="4"
                      transform={`rotate(${rot - 90}, ${tx}, ${ty})`}>
                      {cat.label}
                    </text>
                  );
                })}

                {/* Profile names and traits - reading radially outward */}
                {[
                  { name: "Analyzer", traits: ["Knowledgeable", "Highly Analytical", "Cultured"] },
                  { name: "Philosopher", traits: ["Street Smart", "Observant", "Intuitive"] },
                  { name: "Friend", traits: ["Honourable", "Resilient", "Strong-willed"] },
                  { name: "Leader", traits: ["Inspirational", "Confident", "Proactive"] },
                  { name: "Artist", traits: ["Creative", "Optimistic", "Loyal"] },
                  { name: "Performer", traits: ["Sharp Minded", "High Self Esteem", "Spontaneous"] },
                  { name: "Director", traits: ["Results Oriented", "Straight Forward", "Practical"] },
                  { name: "Pioneer", traits: ["Open Minded", "Risk Taker", "Sociable"] },
                  { name: "Diplomat", traits: ["Fair & Just", "Conservative", "Kind"] },
                  { name: "Warrior", traits: ["Charismatic", "Authoritative", "Adventurous"] },
                ].map((p, i) => {
                  const angle = i * 36 - 90;
                  const midAngle = angle + 18;
                  const midRad = midAngle * Math.PI / 180;
                  // Name near outer edge
                  const nameR = 180;
                  const nx = 250 + nameR * Math.cos(midRad);
                  const ny = 250 + nameR * Math.sin(midRad);
                  // Determine rotation - text reads outward from center
                  const textRot = midAngle + 90;
                  const isActive = openBonus === i;
                  return (
                    <g key={i}>
                      {/* Profile name */}
                      <text x={nx} y={ny} textAnchor="middle" dominantBaseline="central"
                        fill={isActive ? '#6B3A00' : '#222'} fontSize="12" fontWeight="900"
                        transform={`rotate(${textRot}, ${nx}, ${ny})`}>
                        {p.name}
                      </text>
                      {/* Traits - stacked radially inward */}
                      {p.traits.map((t, ti) => {
                        const traitR = 155 - ti * 18;
                        const tx = 250 + traitR * Math.cos(midRad);
                        const ty = 250 + traitR * Math.sin(midRad);
                        return (
                          <text key={ti} x={tx} y={ty} textAnchor="middle" dominantBaseline="central"
                            fill="#444" fontSize="8" fontWeight="500"
                            transform={`rotate(${textRot}, ${tx}, ${ty})`}>
                            • {t}
                          </text>
                        );
                      })}
                    </g>
                  );
                })}

                {/* Clickable segments (on top) */}
                {bonusEnergies.map((b, i) => {
                  const angle = i * 36 - 90;
                  const rad1 = angle * Math.PI / 180;
                  const rad2 = (angle + 36) * Math.PI / 180;
                  const r1 = 72, r2 = 195;
                  const x1 = 250 + r2 * Math.cos(rad1), y1 = 250 + r2 * Math.sin(rad1);
                  const x2 = 250 + r2 * Math.cos(rad2), y2 = 250 + r2 * Math.sin(rad2);
                  const x3 = 250 + r1 * Math.cos(rad2), y3 = 250 + r1 * Math.sin(rad2);
                  const x4 = 250 + r1 * Math.cos(rad1), y4 = 250 + r1 * Math.sin(rad1);
                  const isActive = openBonus === i;
                  return (
                    <path key={i} className="wheel-segment"
                      d={`M${x1},${y1} A${r2},${r2} 0 0,1 ${x2},${y2} L${x3},${y3} A${r1},${r1} 0 0,0 ${x4},${y4} Z`}
                      fill={isActive ? 'rgba(184,134,11,0.3)' : 'transparent'}
                      stroke={isActive ? '#6B3A00' : 'transparent'}
                      strokeWidth={isActive ? 3 : 0}
                      onClick={() => setOpenBonus(isActive ? null : i)}
                    />
                  );
                })}

                {/* Center text */}
                <text x="250" y="237" textAnchor="middle" fill="#D4AF37" fontSize="7" fontWeight="700" letterSpacing="1.5">UNLEASH YOUR</text>
                <text x="250" y="253" textAnchor="middle" fill="white" fontSize="12" fontWeight="900" letterSpacing="1">INTRINSIC</text>
                <text x="250" y="268" textAnchor="middle" fill="white" fontSize="12" fontWeight="900" letterSpacing="1">TALENT</text>

                {/* Inner ring curved text */}
                <path id="curveTop" d="M 168,250 A 82,82 0 0,1 332,250" fill="none" />
                <text fontSize="7" fill="#666" letterSpacing="2" fontWeight="600">
                  <textPath href="#curveTop" startOffset="50%" textAnchor="middle">MAXIMIZE YOUR POTENTIAL</textPath>
                </text>
                <path id="curveBottom" d="M 168,250 A 82,82 0 0,0 332,250" fill="none" />
                <text fontSize="7" fill="#666" letterSpacing="2" fontWeight="600">
                  <textPath href="#curveBottom" startOffset="50%" textAnchor="middle">BUILD HIGH PERFORMANCE TEAMS</textPath>
                </text>
              </svg>
            </div>

            {/* Info Panel */}
            <div className="flex-1 min-w-0">
              {openBonus !== null ? (
                <div className="p-6 bg-white/[0.04] border border-[#D4AF37]/30 rounded-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-orange-400 text-xs font-mono bg-orange-400/10 px-2 py-1 rounded">{bonusEnergies[openBonus].id}</span>
                    <h3 className="text-xl font-bold text-white">{bonusEnergies[openBonus].name}</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-orange-400 text-xs font-bold mb-1">CAREER / BUSINESS</p>
                      <p className="text-gray-300 text-sm">{bonusEnergies[openBonus].career}</p>
                    </div>
                    <div>
                      <p className="text-orange-400 text-xs font-bold mb-1">RELATIONSHIPS</p>
                      <p className="text-gray-300 text-sm">{bonusEnergies[openBonus].relationships}</p>
                    </div>
                    <div>
                      <p className="text-orange-400 text-xs font-bold mb-1">PERSONAL GROWTH</p>
                      <p className="text-gray-300 text-sm">{bonusEnergies[openBonus].growth}</p>
                    </div>
                    <div>
                      <p className="text-red-400 text-xs font-bold mb-1">WATCH OUT FOR</p>
                      <p className="text-gray-400 text-sm">{bonusEnergies[openBonus].watch}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-8 bg-white/[0.02] border border-white/[0.06] rounded-2xl text-center">
                  <p className="text-[#D4AF37] text-lg font-bold mb-2">Tap a Profile</p>
                  <p className="text-gray-500 text-sm">Select any segment on the wheel to discover your Bonus Energy for 2026</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== QIMEN FORECASTING ===== */}
      <AnimatedSection className="relative py-20 px-6 overflow-hidden min-h-[800px] flex flex-col justify-end">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/m49aarbj_ChatGPT%20Image%20Apr%2029%2C%202026%2C%2005_08_27%20PM.png" alt="" aria-hidden="true" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto">
          {/* Content */}
          <div className="text-center mb-12 pt-12 sm:hidden">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">
              <span className="text-[#D4AF37]">Qimen Forecasting</span> <span className="text-white">奇门遁甲</span>
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Navigate opportunities and avoid risks with strategic insight from the ancient Qimen Dunjia system. Align action with time, space, and energy.
            </p>
          </div>

          {/* 4 Pillars */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
            {[
              { title: "ANCIENT WISDOM", desc: "Timeless knowledge for modern decisions." },
              { title: "STRATEGIC INSIGHT", desc: "Navigate opportunities and avoid risks." },
              { title: "TIMING & HARMONY", desc: "Align action with time, space, and energy." },
              { title: "EMPOWER YOUR PATH", desc: "Make confident choices. Shape your future." },
            ].map((item, i) => (
              <div key={i} className="text-center p-5 bg-black/40 backdrop-blur-sm border border-[#D4AF37]/25 rounded-xl hover:border-[#D4AF37]/50 transition-all">
                <h3 className="text-[#D4AF37] font-bold text-sm uppercase tracking-wider mb-2">{item.title}</h3>
                <p className="text-gray-300 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Time Your Success */}
          <div className="text-center mt-12 pb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">Time Your <span className="text-[#D4AF37]">Success & Wealth</span></h3>
            <p className="text-gray-300 max-w-2xl mx-auto text-sm leading-relaxed">
              The key strength of Qi Men Dun Jia lies in its ability to analyse the energy of current space and time. By positioning yourself at the most auspicious energy, at the right time and direction, you can ultimately achieve the best results for your goals – whether in business, relationships, or life decisions.
            </p>
          </div>
        </div>
      </AnimatedSection>



      {/* ===== FINAL CTA ===== */}
      <section className="py-20 px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a0505] via-[#0a0a0a] to-[#0a0a0a]" />
        <div className="absolute inset-0"><div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-red-600/5 rounded-full blur-[120px]" /></div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Make 2026 Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">Most Strategic Year</span>
          </h2>
          <p className="text-gray-400 max-w-lg mx-auto mb-8">The Crimson Horse rewards those who move with courage, clarity, and purpose. Are you ready?</p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400 text-white font-bold uppercase tracking-widest rounded-lg px-10 py-5 text-base transition-all hover:scale-[1.02]">
            <MessageCircle className="w-5 h-5" /> SPEAK WITH MASTER STEVEN <ArrowRight className="w-5 h-5" />
          </a>
          <p className="text-xs text-gray-600 mt-6">Life Decoder · Guide to the Crimson Horse 2026 · Master Steven Yong</p>
        </div>
      </section>
    </div>
  );
};

export default CrimsonHorsePage;
